import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { MessageSquareText, AlertTriangle, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { canAccess } from "@/lib/roles";

export const AutoTextsCardForUser = () => {
  const { user } = useAuth();
  return <AutoTextsTodayCard canOpen={canAccess(user?.role, "messaging")} />;
};

export const AutoTextsTodayCard = ({ className = "mt-6", canOpen = true }) => {
  const navigate = useNavigate();
  const { data } = useQuery({ queryKey: ["auto-texts-today"], refetchInterval: 300000, queryFn: async () => (await api.get("/dashboard/auto-texts-today")).data });
  if (!data) return null;
  const warn = data.problems.length > 0;

  return (
    <motion.button initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.24 }}
      data-testid="dashboard-auto-texts-card" onClick={() => canOpen && navigate("/messaging")}
      className={`${className} flex w-full flex-wrap items-center gap-x-6 gap-y-2 rounded-xl border bg-white p-5 text-left shadow-sm transition-colors ${warn ? "border-red-200 hover:bg-red-50/40" : "border-emerald-200 hover:bg-emerald-50/40"}`}>
      <span className="flex items-center gap-2">
        <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${warn ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-600"}`}>{warn ? <AlertTriangle className="h-[18px] w-[18px]" /> : <MessageSquareText className="h-[18px] w-[18px]" />}</span>
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Automatic texts today</span>
      </span>
      <span data-testid="dashboard-auto-texts-total"><span className="font-mono-metric text-2xl font-bold text-slate-900">{data.total}</span> <span className="text-xs text-slate-500">sent since midnight</span></span>
      {data.by_kind.slice(0, 4).map(([label, n]) => (
        <span key={label} className="text-xs text-slate-500"><b className="text-slate-700">{n}</b> {label}</span>))}
      <span className="text-xs text-slate-500" data-testid="dashboard-auto-texts-run">{data.ran_today ? `Morning run done ${fmtDateTime(data.last_run)}` : data.last_run ? `Last run ${fmtDateTime(data.last_run)}` : "No automatic run recorded yet"}</span>
      {warn && <span className="w-full text-xs font-medium text-red-700" data-testid="dashboard-auto-texts-problems">{data.problems.join(" · ")}</span>}
      {canOpen && <span className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-cyan-700">Open SMS Inbox <ArrowRight className="h-3.5 w-3.5" /></span>}
    </motion.button>
  );
};
